import type {
 Messages } from '../catalogue.en'

/**
 * The Control Center core and the account-profile workspaces, their
 * registries and personal sections. Access administration lives in
 * `controlCenterAccess`, external services in `controlCenterIntegrations`.
 *
 * Composed into the public catalogue by `catalogue.ru.ts`. Nothing
 * imports this module directly: consumers ask the catalogue, so how the
 * messages are stored stays this layer's business.
 */
export const controlCenter: Partial<Messages> = {
  "controlCenter.administration.scopeSharedProfiles": "Общие профили справочников",
  "controlCenter.administration.platformAI": "Сервисы и инструменты ИИ",
  "controlCenter.administration.platformPrintForms": "Инструменты печатных форм",
  "controlCenter.administration.platformTerminology": "Настройка справочников",
  "controlCenter.administration.platformQuotas": "Лимиты клиники",
  "controlCenter.administration.platform": "Администрирование платформы",
  "controlCenter.administration.platformDescription": "Общие ресурсы и технические инструменты",
  "controlCenter.administration.organizationGroup": "Организация",
  "controlCenter.administration.teamGroup": "Сотрудники и доступ",
  "controlCenter.administration.formsGroup": "Формы и справочники",
  "controlCenter.administration.connectionsGroup": "Подключения и ИИ",
  "controlCenter.administration.registration": "Регистрация пациентов",
  "controlCenter.administration.registrationDescription": "Анкета, проверяющий врач и вход пациента",
  "controlCenter.administration.scopePlatform": "Действует для всей платформы",
  "controlCenter.administration.scopeClinic": "Клиника",
  "controlCenter.administration.scopePersonalClinic": "Ваши настройки в клинике",
  "controlCenter.administration.scopeAccount": "Ваша учётная запись",
  "controlCenter.administration.breadcrumb": "Вы здесь",
  "controlCenter.administration.sharedTerminology": "Эти профили могут использоваться несколькими клиниками. Изменение общего профиля меняет его справочники во всех использующих его клиниках.",
  "controlCenter.administration.aiTools": "Инструменты ИИ",
  "controlCenter.administration.aiToolsDescription": "Тестер документов и техническая диагностика",
  "controlCenter.administration.security": "Вход и безопасность",
  "controlCenter.administration.securityDescription": "Имя пользователя, пароль и подтверждённая почта аккаунта",
  "controlCenter.administration.communications": "Уведомления и связь",
  "controlCenter.administration.communicationsDescription": "Мессенджеры, часы уведомлений и подпись сообщений",

  'onboardingSettings.title': 'Анкета пациента',
  'onboardingSettings.loading': 'Загрузка настроек анкеты',
  'onboardingSettings.loadFailed': 'Не удалось загрузить настройки анкеты',
  'onboardingSettings.saveFailed': 'Не удалось сохранить настройки анкеты.',
  'onboardingSettings.invalid': 'Анкета или ответственный врач больше не доступны в этой организации.',
  'onboardingSettings.unsavedIntent': 'Настройки анкеты пациента',
  'onboardingSettings.protocol': 'Анкета',
  'onboardingSettings.reviewer': 'Врач для проверки',
  'onboardingSettings.unassigned': 'Не назначен; уведомлять владельца',
  'onboardingSettings.chooseProtocol': 'Выберите анкету',
  'onboardingSettings.unavailable': 'Недоступно',
  "controlCenter.history.title": "Что нового",
  'controlCenter.history.description': "Задачи, вошедшие в опубликованные релизы. Изменения без номера задачи не перечислены.",
  'controlCenter.history.loading': "Загрузка истории обновлений…",
  'controlCenter.history.empty': "Опубликованных обновлений пока нет.",
  'controlCenter.history.noTasks': "В этом релизе нет зарегистрированных задач.",
  'controlCenter.history.more': "Предыдущие обновления",
  'controlCenter.history.unavailable': "История обновлений недоступна.",
  "controlCenter.title": "Управление клиникой",
  "controlCenter.subtitle": "Настройки выбранной клиники.",
  "controlCenter.navLabel": "Разделы управления клиникой",
  "controlCenter.nav.organization": "Реквизиты",
  "controlCenter.nav.organizationDescription": "Юридическое лицо, от имени которого работает клиника",
  "controlCenter.nav.uploads": "Очередь импорта исследований",
  'controlCenter.nav.uploadsDescription': 'Незавершённые передачи и импорты исследований',
  "controlCenter.nav.users": "Сотрудники",
  'controlCenter.nav.usersDescription': 'Учетные записи, роли и доступ',
  "controlCenter.nav.workplaces": "Работа в филиалах",
  'controlCenter.nav.workplacesDescription': 'Филиалы, профессии и специальности врачей',
  "controlCenter.nav.roles": "Роли и права",
  'controlCenter.nav.rolesDescription': 'Наборы ролей и предоставляемые ими права',
  "controlCenter.nav.settings": "Время, календарь и форматы",
  "controlCenter.nav.settingsDescription": "Часовой пояс, производственный календарь и форматы отображения",
  "controlCenter.nav.terminology": "Справочники и классификаторы",
  'controlCenter.nav.terminologyDescription': 'Словари и версии по разделам',
  "controlCenter.nav.modules": "Модули клиники",
  'controlCenter.nav.modulesDescription': 'Встроенные модули: включён, выключен или скрыт',
  "controlCenter.nav.connectors": "Каналы доставки сообщений",
  'controlCenter.nav.connectorsDescription': 'Коннекторы почты, Telegram и WhatsApp и их учётные данные',
  'controlCenter.nav.printForms': 'Печатные формы',
  'controlCenter.nav.printFormsDescription': 'Зарегистрированные формы и редактор',
  "controlCenter.nav.protocols": "Формы записей и анкеты",
  "controlCenter.nav.protocolsDescription": "Определения, версии и правила выбора",
  "controlCenter.nav.ai": "Функции ИИ",
  'controlCenter.nav.aiDescription': 'Бэкенды, реестр промптов и файлы поддержки',
  'controlCenter.discard': 'Отменить изменения',

  "controlCenter.uploads.title": "Очередь импорта исследований",
  'controlCenter.uploads.subtitle':
    'Все незавершённые импорты исследований клиники, включая приостановленные передачи и ожидающие подтверждения.',
  'controlCenter.uploads.loading': 'Загрузка списка',
  'controlCenter.uploads.failed': 'Не удалось загрузить список. Ни одна строка намеренно не скрыта.',
  'controlCenter.uploads.forbidden': 'Вам не разрешено читать загрузки исследований этой клиники',
  'controlCenter.uploads.tableLabel': 'Незавершённые загрузки исследований',
  'controlCenter.uploads.empty': 'Незавершённых загрузок нет',
  'controlCenter.uploads.emptyDetail': 'Новые импорты появятся здесь сразу после принятия сервером.',
  'controlCenter.uploads.noMatches': 'Нет загрузок, соответствующих фильтрам',
  'controlCenter.uploads.patient': 'Пациент',
  'controlCenter.uploads.source': 'Источник',
  'controlCenter.uploads.uploader': 'Добавил',
  'controlCenter.uploads.updated': 'Обновлено',
  'controlCenter.uploads.openPatientFiles': 'Открыть файлы пациента',
  'controlCenter.uploads.cancel': 'Отменить и удалить',

  "organization.title": "Реквизиты клиники",
  'branches.title': 'Филиалы',
  'branches.name': 'Название',
  'branches.address': 'Адрес',
  'branches.default': 'Основной филиал',
  'branches.active': 'Действует',
  'branches.archived': 'В архиве',
  'branches.create': 'Новый филиал',
  'branches.save': 'Сохранить филиал',
  'branches.archive': 'Архивировать филиал',
  'branches.archiveConfirm': 'Архивировать филиал? История визитов сохранится.',
  'branches.empty': 'Нет филиалов',
  'branches.loadFailed': 'Не удалось загрузить филиалы.',
  'branches.failed': 'Не удалось сохранить филиал. Введённые данные сохранены в форме.',
  'branches.forbidden': 'У вас нет прав на изменение филиалов.',
  'branches.invalid': 'Проверьте название и адрес филиала.',
  'branches.changed': 'Филиал изменился, пока была открыта форма. Загрузите актуальные данные.',
  'branches.defaultRequired': 'Сначала выберите другой действующий филиал основным.',
  'branches.inUse': 'В филиале есть предстоящие приёмы, действующие ресурсы или незавершённые визиты.',
  'branches.reload': 'Обновить данные филиала',
  'branches.reloaded': 'Актуальные данные филиала загружены. Проверьте их перед сохранением.',
  'branches.unsaved': 'Несохранённые данные филиала',
  'branches.subtitle': 'Места, где клиника принимает пациентов.',
  'organization.subtitle': 'Юридическое лицо, от имени которого работает клиника, и история его редакций.',
  'organization.loading': 'Загрузка организации',
  'organization.loadFailed': 'Не удалось загрузить организацию',
  'organization.forbidden': 'Вам не разрешено читать данные организации этой клиники',
  'organization.name': 'Название организации',
  'organization.legalName': 'Юридическое название',
  'organization.country': 'Страна',
  'organization.addressLine1': 'Адрес',
  'organization.addressLine2': 'Уточнение адреса',
  'organization.city': 'Город',
  'organization.postalCode': 'Почтовый индекс',
  'organization.phone': 'Телефон',
  'organization.email': 'Электронная почта',
  'organization.website': 'Веб-сайт',
  'organization.taxType': 'Тип налогового идентификатора',
  'organization.taxNone': 'Не задано',
  'organization.taxIlCompany': 'Номер израильской компании',
  'organization.taxIlVat': 'Израильский номер плательщика НДС',
  'organization.taxIlIdentity': 'Израильский идентификационный номер',
  'organization.taxOther': 'Другой идентификатор',
  'organization.taxValue': 'Налоговый идентификатор',
  'organization.identityLeaf': 'Реквизиты',
  'organization.addressLeaf': 'Адрес',
  'organization.contactsLeaf': 'Контакты',
  'organization.validityLeaf': 'Срок действия',
  'organization.save': 'Сохранить организацию',
  'organization.saving': 'Сохранение',
  'organization.saved': 'Организация сохранена',
  'organization.unsavedIntent': 'Организация',
  'organization.conflict': 'Пока эта форма была открыта, кто-то другой изменил организацию.',
  'organization.saveFailed': 'Не удалось сохранить организацию.',

  "profile.workspaceTitle": "Мой профиль",
  'profile.workspaceSubtitle': 'Ваши персональные данные и настройки учетной записи.',
  'profile.navLabel': 'Разделы профиля',
  "profile.nav.personal": "Личные данные",
  "profile.nav.personalDescription": "Имена, контакты и работа в клинике",
  "profile.nav.tasks": "Меня упомянули",
  'profile.nav.tasksDescription': 'Задачи, в которых вас упоминают',
  "profile.nav.settings": "Личный доступ к ИИ",
  'profile.nav.settingsDescription': 'Ваш личный доступ к ИИ',
  "profile.group.account": "Учётная запись",
  "profile.group.connections": "Подключения и ИИ",
  "profile.group.development": "Разработка",
  "profile.nav.designDrafts": "Черновики дизайна",
  "profile.nav.designDraftsDescription": "Предложения интерфейса, сохранённые помощником",
  'profile.tasksEmpty': 'Вас не упоминает ни одна задача',
  'profile.tasksEmptyDetail': 'Здесь появятся задачи, в сообщении которых вы указаны получателем.',
  'profile.personalTitle': 'Персональная информация',
  "profile.personalSubtitle": "Ваши имена, контакты и работа в выбранной клинике.",
  'profile.professionalTitle': 'Клиническая классификация',
  'profile.loading': 'Загрузка вашего профиля',
  'profile.loadFailed': 'Не удалось загрузить ваш профиль',
  'profile.unsavedIntent': 'Профиль',
  'profile.messageSignature': 'Подпись сообщения',
  'profile.messageSignatureHint':
    'Готовится один раз и добавляется к каждому сообщению, которое вы отправляете пациенту.',
  'profile.passwordTitle': 'Пароль',
  'profile.passwordEnrollTitle': 'Установить пароль',
  'profile.passwordEnrollmentUnsaved': 'Первый пароль',
  'profile.passwordOpenTelegram': 'Подтвердить в Telegram',
  'profile.passwordTelegramFresh': 'Подтверждение Telegram устарело или относится к другому аккаунту. Откройте приложение бота сотрудников заново.',
  'profile.passwordTelegramUnavailable': 'Подтверждение через Telegram недоступно.',
  'profile.passwordAlreadySet': 'У этого аккаунта уже есть пароль.',
  'profile.passwordEnrollmentThrottled': 'Слишком много попыток. Повторите через 15 минут.',
  'profile.passwordEnrollmentRetry': 'Повторить',
  'profile.passwordRequired': 'Для продолжения необходимо сменить пароль.',
  'profile.changePassword': 'Изменить пароль',
  'profile.currentPassword': 'Текущий пароль',
  'profile.newPassword': 'Новый пароль',
  'profile.passwordSave': 'Сохранить пароль',
  'profile.passwordSaving': 'Сохранение',
  'profile.passwordSaved': 'Пароль сохранён',
  'profile.passwordWrong': 'Текущий пароль неверен.',
  'profile.passwordFailed': 'Не удалось сохранить пароль.',
  'profile.passwordUnsavedIntent': 'Смена пароля \u2014 ещё не применена',

  "profileSettings.title": "Личный доступ к ИИ",
  "profileSettings.subtitle": "Ваш ключ OpenAI для выбранной клиники. Он не переносится в другие клиники.",
  'profileSettings.openAISecretTitle': 'Ключ OpenAI',
  'profileSettings.secretUnsavedIntent': 'Ключ OpenAI',
  'profileSettings.secretLoading': 'Загрузка статуса ключа OpenAI',
  'profileSettings.secretUnavailable': 'Хранилище секретов не настроено.',
  'profileSettings.secretLoadFailed': 'Не удалось загрузить статус ключа OpenAI.',
  'profileSettings.key': 'Ключ',
  'profileSettings.secretSet': 'Установить ключ',
  'profileSettings.secretChange': 'Заменить ключ',
  'profileSettings.openAISecret': 'Ключ API OpenAI',
  'profileSettings.secretSave': 'Сохранить ключ',
  'profileSettings.secretSaving': 'Сохранение',
  'profileSettings.secretClear': 'Очистить ключ',
  'profileSettings.secretClearing': 'Очистка',
  'profileSettings.secretSaved': 'Ключ OpenAI сохранён',
  'profileSettings.secretCleared': 'Ключ OpenAI очищен',
  'profileSettings.secretSaveFailed': 'Не удалось сохранить ключ OpenAI.',
  'profileSettings.secretClearFailed': 'Не удалось удалить ключ OpenAI.',

  "clinicSettings.title": "Время, календарь и форматы",
  'clinicSettings.subtitle': 'Гражданское время клиники и отображение даты, времени и чисел.',
  'clinicSettings.loading': 'Загрузка настроек клиники',
  'clinicSettings.loadFailed': 'Не удалось загрузить настройки клиники',
  'clinicSettings.forbidden': 'Вам не разрешено читать настройки этой клиники',
  'clinicSettings.unsavedIntent': 'Настройки клиники',
  'clinicSettings.timeZone': 'Часовой пояс клиники',
  'clinicSettings.workingCalendar': 'Производственный календарь клиники',
  'clinicSettings.dateFormat': 'Формат даты',
  'clinicSettings.dateFormat.dayMonthYear': '23/08/2026',
  'clinicSettings.dateFormat.monthDayYear': '08/23/2026',
  'clinicSettings.dateFormat.yearMonthDay': '2026-08-23',
  'clinicSettings.timeFormat': 'Формат времени',
  'clinicSettings.timeFormat.24Hour': '24 часа (14:30)',
  'clinicSettings.timeFormat.12Hour': '12 часов (2:30 PM)',
  'clinicSettings.numberFormat': 'Формат чисел',
  'clinicSettings.numberFormat.decimalDotGroupComma': '1,234.56',
  'clinicSettings.numberFormat.decimalCommaGroupSpace': '1 234,56',
  'clinicSettings.numberFormat.decimalCommaGroupDot': '1.234,56',
  'clinicSettings.saveFailed': 'Не удалось сохранить настройки клиники.',

  'clinicSettings.civilTime': 'Гражданское время и производственный календарь',
  'clinicSettings.displayFormats': 'Форматы отображения',

  'clinicSettings.services': 'Подключённые сервисы',
  'clinicSettings.servicesSubtitle':
    'Внешние сервисы, для которых у этой клиники есть учётные данные. Сохранённый секрет больше никогда не показывается — только его первый и последний символы, чтобы можно было узнать сохранённый.',

  'terminology.profile': "Профиль",
  'terminology.profileSetupRequired': 'Профиль терминологии не настроен. Требуется настройка разработчиком.',
  'terminology.profileSelected': "Выбранный профиль",
  'terminology.profileSelect': "Выбрать для клиники",
  'terminology.profileEdit': "Изменить профиль",
  'terminology.profileCopy': "Создать копию",
  'terminology.profileDelete': "Удалить неиспользуемый профиль",
  'terminology.profileName': "Название",
  'terminology.profileCountry': "Страна (код ISO)",
  'terminology.profileShared': "Изменения применяются ко всем клиникам, использующим этот профиль.",
  'terminology.profileUnsaved': "Несохранённый профиль терминологии",
  'terminology.profileConflict': "Профиль или выбор клиники изменился. Перечитайте сохранённую редакцию перед сохранением.",
  'terminology.profileReload': "Перечитать и сбросить изменения профиля",
  'terminology.profileReloadFailed': "Не удалось перечитать. Черновик сохранён.",
  'terminology.profileReloaded': "Сохранённый профиль и выбор клиники загружены. Проверьте их перед сохранением.",
  'terminology.profileFailed': "Не удалось сохранить. Проверьте страну, использующие профиль клиники и актуальную редакцию; черновик сохранён.",
  'terminology.profileCreate': "Создать профиль",
  'terminology.profileUnbind': "Убрать привязку",
  "terminology.title": "Справочники и классификаторы",
  'terminology.subtitle': 'Какой словарь читает каждая часть продукта и в какой версии.',
  'terminology.loading': 'Загрузка привязок терминологии',
  'terminology.loadFailed': 'Не удалось загрузить привязки терминологии',
  'terminology.forbidden': 'Вам не разрешено читать привязки терминологии этой клиники',
  'terminology.serviceUnavailable':
    'Сервис терминологии не отвечает. Ниже показаны привязки, сохранённые этой клиникой; выбрать словарь или версию нельзя, пока он не вернётся.',

  'terminology.openApiReference': 'Открыть справочник API терминологии',
  'terminology.openApiReferenceNote':
    'Справочник откроется в новой вкладке. Он описывает сам сервис терминологии, а не этот реестр.',

  'terminology.section': 'Раздел',
  'terminology.sectionDiagnoses': 'Диагнозы',
  'terminology.sectionOncologyTopography': 'Онкологическая топография',
  'terminology.sectionOncologyTNM': 'Онкология: TNM',
  'terminology.sectionOncologyMorphology': 'Онкологическая морфология',
  'terminology.sectionLaboratory': 'Лаборатория',
  'terminology.sectionServices': 'Медицинские услуги',
  'terminology.sectionPractitionerProfessions': 'Профессии врачей',
  'terminology.sectionOrganizationStructure': 'Организационная структура',
  'terminology.sectionAllergyDrugSubstances': 'Анамнез: лекарственные аллергены',
  'terminology.sectionAllergyOtherSubstances': 'Анамнез: прочие аллергены',
  'terminology.sectionAllergyManifestations': 'Анамнез: аллергические реакции',
  'terminology.sectionHabits': 'Анамнез: вредные привычки',

  'terminology.dictionary': 'Словарь',
  'terminology.dictionaryFor': 'Словарь для {section}',
  'terminology.version': 'Версия',
  'terminology.versionFor': 'Версия для {section}',
  'terminology.versionFollows': 'Следовать текущей',
  'terminology.versionUnpublished': '{version} (больше не публикуется)',
  'terminology.versionsLoading': 'Загрузка версий',
  'terminology.versionsFailed': 'Не удалось загрузить версии этого словаря',

  'terminology.resolvedVersion': 'Действует',

  'terminology.status': 'Статус',
  'terminology.statusActive': 'Используется',
  'terminology.statusRecorded': 'Только записано',

  'terminology.updated': 'Изменено',

  'terminology.actions': 'Действия',
  'terminology.editNamed': 'Изменить {name}',
  'terminology.save': 'Сохранить',
  'terminology.saving': 'Сохранение',
  'terminology.cancel': 'Отмена',
  'terminology.reload': 'Перечитать реестр',
  'terminology.unavailableEdit': 'Сервис терминологии не отвечает',

  'terminology.saveFailed': 'Не удалось сохранить привязку',
  'terminology.saveForbidden': 'Вам не разрешено менять привязки терминологии этой клиники',
  'terminology.saveConflict':
    'Кто-то другой изменил эту привязку. Перечитайте реестр и решайте, исходя из того, что хранится сейчас.',
  'terminology.saveRejected': 'Этот словарь и версию нельзя привязать к этому разделу',

  'terminology.tableLabel': 'Терминологические привязки',
  'terminology.empty': 'Ни один раздел продукта не использует терминологию',
  'terminology.emptyDetail': 'Список разделов приходит из продукта, а не из этой клиники.',
  'terminology.noMatches': 'Ни один раздел не соответствует фильтрам',
  'terminology.noMatchesDetail': 'Сбросьте фильтры, чтобы увидеть все разделы.',

  "protocols.title": "Формы записей и анкеты",
  'protocols.subtitle': 'Опубликованные определения, активация и детерминированное назначение для данной клиники.',
  'protocols.loading': 'Загрузка протоколов и типов приёмов',
  'protocols.loadFailed': 'Не удалось загрузить реестр протоколов',
  'protocols.forbidden': 'Вам не разрешено читать протоколы этой клиники',
  'protocols.openEditor': 'Открыть редактор протоколов',
  'protocols.openEditorHint': 'Редактор откроется в новой вкладке под этой учётной записью.',
  'protocols.releasesTitle': 'Зарегистрированные релизы',
  'protocols.releasesEmpty': 'Для этой клиники не зарегистрирован ни один релиз MPE.',
  'protocols.protocol': 'Протокол',
  'protocols.release': 'Релиз',
  'protocols.registered': 'Зарегистрировано',
  'protocols.state': 'Состояние',
  'protocols.actions': 'Действия',
  'protocols.active': 'Активный',
  'protocols.inactive': 'Неактивный',
  'protocols.activate': 'Активировать релиз',
  'protocols.deactivate': 'Снять с выбора',
  'protocols.policyTitle': 'Политика отбора',
  'protocols.policyHint':
    'Применяется точное правило по специальности места работы и типу приёма. Протокол клиники по умолчанию используется, только когда точное правило не найдено.',
  'protocols.defaultLabel': 'По умолчанию для клиники',
  'protocols.chooseProtocol': 'Выберите активный протокол',
  'protocols.saveDefault': 'Сохранить умолчание',
  'protocols.expertise': 'Специальность',
  'protocols.visitType': 'Тип приёма',
  'protocols.chooseVisitType': 'Выберите тип приёма',
  'protocols.addRule': 'Добавить точное правило',
  'protocols.rulesEmpty': 'Точные правила назначения не заданы.',
  'protocols.deleteRuleNamed': 'Удалите правило назначения для {name}',
  'protocols.deleteSelected': 'Удалить выделенные ({count})',
  'protocols.deleteSelectedTitle': 'Удалить выделенные правила назначения?',
  'protocols.deleteSelectedDescription':
    'Правила ({count}) перестанут назначать протокол. У уже начатых приёмов протокол сохранится.',
  'protocols.deleteSelectedConfirm': 'Удалить правила ({count})',
  'protocols.deleteSelectedCancel': 'Оставить выделение',
  'protocols.rulesDeletedCount': 'Правил назначения удалено: {count}.',
  'protocols.rulesDeletedBefore': 'Правил удалено: {count}.',
  'protocols.actionFailed': 'Не удалось выполнить команду.',
  'protocols.activateFailed': 'Не удалось активировать релиз.',
  'protocols.defaultFailed': 'Не удалось изменить протокол по умолчанию для клиники.',
  'protocols.createRuleFailed': 'Не удалось добавить правило.',
  'protocols.deleteRuleFailed': 'Не удалось удалить правило.',
  'protocols.activateRequiresPrimary':
    'Протокол, который использует политика отбора, должен остаться основным документом приёма.',
  'protocols.defaultRequiresPrimary':
    'По умолчанию для клиники можно выбрать только активный основной документ приёма.',
  'protocols.releaseGone': 'Этот релиз больше не зарегистрирован. Обновите реестр.',
  'protocols.deactivateFailed': 'Не удалось снять протокол с выбора.',
  'protocols.deactivateUsedByRule':
    'Протокол указан в правиле назначения. Сначала удалите правило.',
  'protocols.deactivateIsDefault':
    'Это протокол клиники по умолчанию. Сначала выберите другое умолчание.',
  'protocols.activationGone': 'Этот протокол уже не активен. Обновите реестр.',
  'protocols.ruleGone': 'Это правило уже удалено. Обновите реестр.',
  'protocols.ruleExists': 'Правило для этой специальности и типа приёма уже существует.',
  'protocols.ruleRejected':
    'Правило отклонено. Проверьте специальность, тип приёма и протокол.',
  'protocols.registryUnavailable': 'Реестр протоколов недоступен. Повторите чуть позже.',
  'protocols.suggestionStale': 'Сохранённое имя изменилось. Откройте тип заново и запросите новые подсказки.',
  'protocols.unsavedIntent': 'Форма расписания \u2014 данные, которые не были сохранены',
  'protocols.visitTypesTitle': 'Типы приёмов',
  'protocols.visitTypesHint':
    'Стабильный идентификатор типа используется правилами протоколов. Опубликованные версии сохраняют длительность и историю отображения.',
  'protocols.visitTypesForbidden': 'Вам не разрешено читать типы приёмов этой клиники',
  'protocols.visitTypesEmpty': 'Типы приёмов не зарегистрированы.',
  'protocols.visitTypeCode': 'Стабильный код',
  'protocols.visitTypeName': 'Отображаемое название',
  'protocols.duration': 'Длительность, минуты',
  'protocols.requirementCode': 'Код требования',
  'protocols.capabilityCode': 'Код возможности',
  'protocols.requirementQuantity': 'Количество',
  'protocols.addRequirement': 'Добавить требование к ресурсу',
  'protocols.removeRequirement': 'Удалить',
  'protocols.createVisitType': 'Создать тип приёма',
  'protocols.versions': 'Опубликованные версии',
  'protocols.versionSummary': 'v{version} · {minutes} мин',
  'protocols.publishVersion': 'Опубликовать версию',
  'protocols.rulesTableLabel': 'Точные правила назначения',
  'protocols.newRuleTitle': 'Новое правило назначения',
  'protocols.newVisitTypeTitle': 'Новый тип приёма',
  'protocols.publishVersionTitle': 'Опубликовать версию',

  'printForm.title': 'Печатные формы',
  'printForm.subtitle': 'Печатные формы, зарегистрированные этой клиникой, и релиз, из которого печатается каждая.',

  'printForm.loading': 'Загрузка печатных форм',
  'printForm.loadFailed': 'Не удалось загрузить печатные формы',
  'printForm.forbidden': 'Вам не разрешено читать печатные формы этой клиники',
  'printForm.bindingsEmpty': 'В этой клинике ещё не зарегистрирована ни одна печатная форма',

  'printForm.formsTableLabel': 'Зарегистрированные печатные формы',
  'printForm.form': 'Форма',
  'printForm.bindingCode': 'Код формы',
  'printForm.contextSchema': 'Схема данных',
  'printForm.anchorTypes': 'Печатается из',
  'printForm.bindingStatus': 'Статус формы',
  'printForm.bindingActive': 'Используется',
  'printForm.bindingInactive': 'Отозвано',
  'printForm.actions': 'Действия',
  'printForm.detailsNamed': 'Сведения о {name}',
  'printForm.detailsTitle': 'Сведения о форме',

  'printForm.currentRelease': 'Текущий релиз',

  'printForm.currentReleaseNamed': 'Текущий релиз {version}',
  'printForm.noCurrentRelease': 'Для этой формы не активирован ни один релиз',
  'printForm.releaseAbsent': 'Не активировано',
  'printForm.releaseReadFailed': 'Не удалось прочитать текущий релиз этой формы',
  'printForm.releaseUnread': 'Не прочитано',
  'printForm.releaseVersion': 'Релиз',
  'printForm.pointerRevision': 'Редакция указателя',
  'printForm.publishedAt': 'Опубликовано',
  'printForm.activatedAt': 'Активировано',
  'printForm.publishedBy': 'Опубликовал',
  'printForm.activatedBy': 'Активировал',
  'printForm.provider': 'Собрано',
  'printForm.runtimeApi': 'API среды выполнения',
  'printForm.bundleSchema': 'Схема пакета',
  'printForm.bundleHash': 'Хэш пакета',

  'printForm.readiness': 'Печать',
  'printForm.readinessReady': 'Готово',
  'printForm.readinessNotReady': 'Не готово',

  'printForm.schemasTitle': 'Схемы данных',
  'printForm.schemasEmpty': 'В этой клинике пока не опубликована ни одна схема данных',
  'printForm.schemaName': 'Схема',
  'printForm.schemaVersionColumn': 'Версия',
  'printForm.schemaCode': 'Код',
  'printForm.schemaDescription': 'Описание',
  'printForm.schemaDigest': 'Семантический хэш',
  'printForm.compatibilityTitle': 'Маршруты совместимости',
  'printForm.compatibilityEmpty': 'Нет активных совместимых маршрутов источников',
  'printForm.compatibilityLanguage': 'Язык',
  'printForm.compatibilitySource': 'Контракт источника',
  'printForm.compatibilityBuilder': 'Сборщик',
  'printForm.compatibilityLegacyIncomplete': 'Старых записей с неполным историческим контекстом печати: {count}',
  'printForm.compatibilitySummary':
    'В инвентаризации: версий источников — {sources}, сборщиков — {builders}, выпусков — {releases}',
  'printForm.compatibilityIssuesTitle': 'Незавершённые работы по совместимости',
  'printForm.compatibilityIssuesEmpty': 'Неподдерживаемых исторических контрактов печати не найдено',
  'printForm.compatibilityIssueBuilder': '{identity}: совместимый сборщик контекста не зарегистрирован ({count})',
  'printForm.compatibilityIssueRoute': '{identity}: у черновиков нет активного маршрута источника ({count})',
  'printForm.compatibilityIssueHistory': '{identity}: исторический контекст печати неполон ({count})',
  'printForm.compatibilityIssueRelease': '{identity}: семантика выпущенного контекста недоступна ({count})',

  'printForm.editorOpen': 'Открыть редактор печатных форм',
  'printForm.editorOpensInNewTab': 'Редактор откроется в новой вкладке под этой учётной записью.',
  'printForm.editorChecking': 'Проверка редактора печатных форм',
  'printForm.editorUnavailable': 'Редактор печатных форм не отвечает',
  'printForm.editorUnavailableDetail':
    'Он работает как отдельный сервис. На зарегистрированное здесь это не влияет; попробуйте снова, когда он вернётся.',

  'printForm.editorPrivileges': 'В редакторе вы: {privileges}',
  'printForm.privilegeEditor': 'редактор',
  'printForm.privilegePublisher': 'издатель',
  "quotas.title": "Лимиты и использование",
  "quotas.description": "Лимиты организации и текущее использование.",
  "quotas.employees": "Сотрудники",
  "quotas.branches": "Филиалы",
  "quotas.storage": "Хранение",
  "quotas.storageGB": "Хранение, ГБ",
  "quotas.gb": "{value} ГБ",
  "quotas.unlimited": "Без ограничений",
  "quotas.used": "Использовано",
  "quotas.limit": "Лимит",
  "quotas.reserved": "Зарезервировано",
  "quotas.remaining": "Доступно",
  "quotas.stateOk": "В пределах лимита",
  "quotas.stateTight": "Близко к лимиту",
  "quotas.stateFull": "Лимит исчерпан",
  "quotas.stateOver": "Лимит превышен",
  "quotas.usedPercent": "Использовано {percent}%",
  "quotas.overLimit": "Использование превышает лимит. Данные сохранены; увеличение заблокировано.",
  "quotas.estimate": "Хранение включает файлы и устойчивую оценку объёма базы данных. 1 ГБ = 1 000 000 000 байт.",
  "quotas.readOnly": "Хранилище заполнено. Чтение и скачивание доступны. Освободите место или запросите увеличение лимита, чтобы продолжить изменения.",
  "quotas.loadFailed": "Не удалось загрузить квоты.",
  "quotas.saveFailed": "Лимиты не сохранены. Изменения остались в форме.",
  "quotas.saved": "Лимиты сохранены.",
  "quotas.save": "Сохранить лимиты",
  "quotas.reload": "Загрузить лимиты заново и сбросить изменения формы",
  "quotas.reason": "Причина изменения",
  "quotas.conflict": "Лимиты изменились на сервере. Ваши изменения остались в форме. Загрузите актуальные лимиты перед повторной попыткой.",
  "quotas.invalid": "Укажите корректные лимиты: не менее одного филиала, неотрицательные значения сотрудников и хранения.",
  "quotas.unsaved": "Несохранённые лимиты квот",
  "quotas.emptyUnlimited": "Оставьте лимит пустым для снятия ограничения.",
  "quotas.exceeded": "Достигнута квота организации. Несохранённые изменения остались доступны.",
  'quotas.dismiss': 'Закрыть уведомление',
}
